import { useEffect, useState } from 'react';
import { getSystemSetting, SETTING_KEYS } from '../api/systemSettings';

/** Phone-sized breakpoint. Adjust here if tablets should be blocked too. */
const SMALL_QUERY = '(max-width: 767px)';

/**
 * Returns true when BOTH:
 *   1. an admin has enabled the `ui.block_small_devices` system setting, and
 *   2. the current viewport is phone-sized (reacts live to resize/rotate).
 *
 * Defaults to NOT blocking until the setting resolves, so a slow fetch never
 * hides the app incorrectly. Used by SessionGate to gate the logged-in app
 * (public landing/login pages are never affected).
 */
export function useSmallDeviceBlock(enabled: boolean = true): boolean {
  const [settingOn, setSettingOn] = useState(false);
  const [narrow, setNarrow] = useState<boolean>(
    () => typeof window !== 'undefined' && window.matchMedia(SMALL_QUERY).matches,
  );

  useEffect(() => {
    // Don't probe the setting until enabled (e.g. authenticated). Probing on the
    // public landing page produced a stray 404 and the block never applies there.
    if (!enabled) return;

    let active = true;
    getSystemSetting(SETTING_KEYS.BLOCK_SMALL_DEVICES)
      .then((v) => { if (active) setSettingOn(v === 'true'); })
      .catch(() => { /* default: don't block */ });

    const mq = window.matchMedia(SMALL_QUERY);
    const onChange = (e: MediaQueryListEvent) => setNarrow(e.matches);
    mq.addEventListener('change', onChange);
    return () => {
      active = false;
      mq.removeEventListener('change', onChange);
    };
  }, [enabled]);

  return enabled && settingOn && narrow;
}
